"use client";

import { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import { createClient } from "@/utils/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ThumbsUp, Share2 } from "lucide-react";
import type { GameType } from "@/lib/types";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";

interface GameInfoProps {
  game: GameType;
}

export function GameInfo({ game }: GameInfoProps) {
  const supabase = createClient();
  const router = useRouter();

  const [currentUrl, setCurrentUrl] = useState("");
  const [localLikes, setLocalLikes] = useState(game.likes || 0);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoadingLike, setIsLoadingLike] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string>("플레이 가능함");

  // 카테고리 한글 매핑
  const categoryMapping: Record<string, string> = {
    Puzzle: "퍼즐",
    Arcade: "아케이드",
    Strategy: "전략",
    "Tower Defense": "타워 디펜스",
    Classic: "클래식",
    Shooter: "슈팅",
    Racing: "레이싱",
    Sports: "스포츠",
    Action: "액션",
    Survival: "생존",
    "Match 3": "매치 3",
    Platformer: "플랫포머",
    Adventure: "어드벤처",
    Word: "단어",
    Simulation: "시뮬레이션",
    Board: "보드",
  };

  // 현재 페이지 URL
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCurrentUrl(window.location.href);
    }
  }, []);

  // 초기 좋아요 상태 및 사용자 정보 로드
  useEffect(() => {
    setLocalLikes(game.likes || 0);
    // localStorage에서 좋아요 여부 확인
    const likedGames = JSON.parse(localStorage.getItem('liked_games') || '[]');
    setIsLiked(likedGames.includes(game.id));

    // 사용자 정보 및 관리자 여부 확인
    const fetchUserAndAdmin = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      if (user) {
        const { data: roleData } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .single();
        setIsAdmin(roleData?.role === "admin");
      } else {
        setIsAdmin(false);
      }
    };
    fetchUserAndAdmin();

    // 게임 상태 로드 (기존 코드)
    const fetchGameStatus = async () => {
      const { data, error } = await supabase
        .from("game_statuses")
        .select("status")
        .eq("game_id", game.id)
        .single();
      if (!error && data) {
        setCurrentStatus(data.status);
      }
    };
    fetchGameStatus();

  }, [game.id, game.likes, supabase]);

  // 좋아요 핸들러
  const handleLike = async () => {
    if (isLoadingLike) return; // 중복 클릭 방지
    if (!currentUser) {
      toast.error("좋아요를 누르려면 로그인이 필요합니다.");
      router.push('/account/login'); // 로그인 페이지로 이동
      return;
    }
    if (isLiked) {
      toast.info("이미 좋아요를 누른 게임입니다.");
      return;
    }

    setIsLoadingLike(true);
    const newLikes = localLikes + 1;

    // Optimistic UI Update
    setLocalLikes(newLikes);
    setIsLiked(true);
    const likedGames = JSON.parse(localStorage.getItem('liked_games') || '[]');
    localStorage.setItem('liked_games', JSON.stringify([...likedGames, game.id]));

    try {
      const response = await fetch(`/api/games/${game.id}/stats`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type: 'likes', value: newLikes }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || '좋아요 업데이트 실패');
      }
      // 성공 시 특별한 처리 없음 (이미 UI 업데이트 됨)
      toast.success("게임을 좋아합니다!");
    } catch (error: any) {
      console.error("좋아요 업데이트 오류:", error);
      toast.error(`좋아요 실패: ${error.message}`);
      // Rollback optimistic update
      setLocalLikes(localLikes);
      setIsLiked(false);
      const updatedLikedGames = JSON.parse(localStorage.getItem('liked_games') || '[]').filter((id: string) => id !== game.id);
      localStorage.setItem('liked_games', JSON.stringify(updatedLikedGames));
    } finally {
      setIsLoadingLike(false);
    }
  };

  // 공유 핸들러
  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(currentUrl).then(() => {
        Swal.fire({ icon: "success", title: "URL 복사됨!", confirmButtonText: "확인" });
      });
    } else {
      Swal.fire({ icon: "error", title: "복사 실패", text: "수동으로 복사해주세요.", confirmButtonText: "확인"});
    }
  };

  // 관리자인 경우 관리자 페이지로 이동하는 핸들러
  const handleAdminPage = () => {
    router.push(`/games/${game.slug}/admin`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-primary">게임 정보</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 설명 */}
        <div>
          <h3 className="text-lg font-semibold mb-2">설명</h3>
          <p className="text-sm text-foreground">
            {game.description?.trim() || "미등록"}
          </p>
        </div>

        <Separator />

        {/* 카테고리 */}
        <div>
          <h3 className="text-lg font-semibold mb-2">카테고리</h3>
          <div className="flex flex-wrap gap-2">
            {game.categories
              ?.filter(category => category && category.trim() !== '') // 유효한 카테고리만 필터링
              .map((category) => (
                <Badge key={category} variant="outline">
                  {categoryMapping[category] || category}
                </Badge>
              ))}
          </div>
        </div>

        <Separator />

        {/* 통계 */}
        <div>
          <h3 className="text-lg font-semibold mb-2">통계</h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">조회수:</span>
              <span className="ml-1 font-semibold">
                {game.views ?? 0}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">좋아요:</span>
              <span className="ml-1 font-semibold">
                {localLikes.toLocaleString()}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">추가일:</span>
              <span className="ml-1 font-semibold">
                {game.dateAdded
                  ? new Date(game.dateAdded).toISOString().split("T")[0]
                  : "확인 안됨"}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">업로드:</span>
              <span className="ml-1 font-semibold">
                {game.developer || "확인 안됨"}
              </span>
            </div>
          </div>
        </div>

        <Separator />

        {/* 게임 실행 상태 */}
        <div>
          <h3 className="text-lg font-semibold mb-2">게임 실행 상태</h3>
          <p className="text-sm text-foreground">
            {currentStatus || "확인 안됨"}
          </p>
        </div>

        <Separator />

        {/* 좋아요 / 공유 버튼 */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleLike} disabled={isLoadingLike || isLiked}>
            <ThumbsUp className={`h-4 w-4 mr-1 ${isLiked ? 'fill-primary' : 'text-primary/70'}`} />
            {isLiked ? '좋아요 완료' : '좋아요'}
          </Button>
          <Button variant="outline" size="sm" onClick={handleShare}>
            <Share2 className="h-4 w-4 mr-1" />
            공유
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={handleAdminPage}>
              관리자 페이지
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
